<template>
  <!--
    A band, not a screen. Matt: "the worker distribution UI is going to have
    to be a lot smaller, maybe just a band across the top, shorter text, make
    it clear but small so that I can live up there and I can still see in a
    lower pane my player board."
  -->
  <div class="alloc">
    <div class="alloc__head">
      <!-- A moment, not a strip (Matt, playtest 10: "not really clear to me
           that it's a special moment... how many we have to give out"). -->
      <strong class="alloc__title">{{ dealt ? 'The first dawn' : 'Dawn' }} · {{ total }} {{ total === 1 ? 'worker' : 'workers' }}{{ dealt ? '' : ' to share out' }}</strong>
      <span class="alloc__hint">
        <template v-if="dealt">the split is dealt today</template>
        <template v-else-if="left > 0">{{ left }} still to give</template>
        <template v-else>all {{ total }} given</template>
      </span>
      <!-- A verb (playtest 13, Nick read "Same as yesterday" as a statement). -->
      <button v-if="!readonly && lastSplit && !firstRound" class="linkish alloc__same" @click="$emit('propose', lastSplit)">Use yesterday's split</button>
    </div>
    <ul class="alloc__chips">
      <!-- The tutorial points at your own chip ('alloc-mine') and the start.
           Holding two roles, the chips ARE the tabs: the whole chip switches
           the board below, and the other one you hold wears its colour
           (playtest 11: "I can't see the Frontier board at all"). -->
      <li v-for="role in ROLES" :key="role" class="alloc-chip" :class="{ 'alloc-chip--viewing': role === viewing, 'alloc-chip--tab': mine.length > 1 && mine.includes(role) && role !== viewing }" :style="{ '--role': ROLE_INFO[role].colour }" :data-tut="role === viewing ? 'alloc-mine' : null" @click="mine.includes(role) && $emit('view', role)">
        <button class="alloc-chip__role" :disabled="!mine.includes(role)" :title="mine.includes(role) ? `See the ${ROLE_INFO[role].label} board below` : null">{{ ROLE_INFO[role].label }}</button>
        <!-- THE FIRST DAWN IS DEALT (playtest 14: round one's split "is not
             even an interesting decision"): no steppers on day one. -->
        <span class="alloc-chip__controls">
          <button v-if="!dealt" class="alloc__btn" :disabled="readonly || allocation[role] <= 0" :aria-label="`One fewer for the ${ROLE_INFO[role].label}`" @click="$emit('allocate', { role, delta: -1 })">−</button>
          <span class="alloc-chip__count">{{ allocation[role] }}</span>
          <button v-if="!dealt" class="alloc__btn" :disabled="readonly || left <= 0" :aria-label="`One more for the ${ROLE_INFO[role].label}`" @click="$emit('allocate', { role, delta: 1 })">+</button>
        </span>
        <!-- What one more worker would buy this role (playtest 14), or the
             need it declared: the ask a player would make if they knew. -->
        <small class="alloc-chip__need" :class="{ 'alloc-chip__need--declared': game.needs?.[role] }">+1: {{ asks[role] }}</small>
        <!-- A published theory's student turns out for this role today, on
             top of the split (Matt, playtest 10: "show the extra workers"). -->
        <small v-if="game.boons?.[role]" class="alloc-chip__boon" :title="`+${game.boons[role]} from a theory that held`">+{{ game.boons[role] }} student</small>
      </li>
    </ul>
    <div v-if="!readonly" class="alloc__go">
 <button class="primary alloc__start" data-tut="alloc-start" :disabled="(total > 0 && given === 0) || tapped" @click="$emit('start')">{{ total > 0 && given === 0 ? 'Give out workers first' : (tapped ? `✓ You're ready · waiting on ${waitingNames}` : (left > 0 ? `Start the day (${left} stay home)` : 'Start the day')) }}
        <!-- Whose button it is (the learner: every run waited, unsure). Everyone
             taps it since playtest 10 (Matt: "a nice little moment of unification"). -->
        <!-- Who has tapped already (Matt, playtest 13: "the button should
             reflect who has already pressed it"). -->
        <small class="action__sub">{{ tapped ? 'the day starts when everyone has tapped' : (readyNames ? `${readyNames} ready - the day starts when all have tapped` : 'split looks right? everyone taps this; the day starts when all have') }}</small>
      </button>
      <!-- Somebody stepped away: start with the taps so far (like Decide now on a vote). -->
      <button v-if="tapped" class="linkish alloc__force" @click="$emit('start', { force: true })">
        Start without them
      </button>
    </div>
    <!-- What a worker IS, for the role you are looking at (playtest 9: "one
         of the workers - I don't know what that means"). Always, not just
         round one: a short line, and it changes with the chip you view. -->
    <!-- ...or, when the people are owed something, THAT: it is what the
         split should be planning for (engine/demands.js). -->
    <!-- A student is an extra worker a held theory sends (Andrew, playtest
         13: "oh, is that how that works"). -->
    <p v-if="students.length" class="alloc__first alloc__student">A theory held, so the Scholars send a student: +1 worker for the {{ students.join(' and the ') }} today, on top of the split.</p>
    <p v-if="demand" class="alloc__first alloc__demand"><b>The people want {{ demand.label.charAt(0).toLowerCase() + demand.label.slice(1) }}</b> - they will ask {{ demand.when }}.</p>
    <p v-else-if="dealt" class="alloc__first">{{ workerMeans }} Day one's split is dealt; from tomorrow the table decides it.</p>
    <p v-else class="alloc__first">{{ workerMeans }} Anyone can move the split; plan on your board below.</p>
  </div>
</template>

<script>
import { ROLES, ROLE_INFO } from '@/engine/constants';
import { allocationOf } from '@/engine/game';
import { asksOf } from '@/engine/asks';
import { demandWhen } from '@/engine/demands';

// Dawn: the workers are divided. The split lives in the game state, so every
// phone and the table watch the same numbers move.
export default {
  name: 'AllocationPicker',
  props: {
    game: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    // Which role's board is previewed underneath, and which are this phone's.
    viewing: { type: String, default: null },
    mine: { type: Array, default: () => [] },
    // This phone has tapped Start and waits on the names given.
    tapped: { type: Boolean, default: false },
    waitingNames: { type: String, default: '' },
    // Who has tapped Start already (before this phone has).
    readyNames: { type: String, default: '' },
  },
  emits: ['allocate', 'propose', 'start', 'view'],
  data() { return { ROLES, ROLE_INFO }; },
  computed: {
    demand() {
      const d = this.game.civ?.demand;
      return d ? { label: d.label, when: demandWhen(d, this.game.meta.round) } : null;
    },
    workerMeans() {
      // One wording, shared with the tutorial's dawn card (ROLE_INFO.worker).
      return ROLE_INFO[this.viewing]?.worker || 'A worker is one person\'s day; most actions cost one.';
    },
    allocation() { return allocationOf(this.game); },
    students() { return ROLES.filter((r) => this.game.boons?.[r]).map((r) => ROLE_INFO[r].label); },
    total() { return this.game.civ.workers; },
    given() { return ROLES.reduce((n, r) => n + this.allocation[r], 0); },
    left() { return Math.max(0, this.total - this.given); },
    firstRound() { return this.game.meta.round === 1; },
    // Day one's split is the game's (a practice day keeps its steppers: the sandbox sets its own).
    dealt() { return this.firstRound && !this.game.tutorial; },
    asks() { return asksOf(this.game); },
    lastSplit() {
      const last = [...this.game.log].reverse().find((l) => l.kind === 'order');
      if (!last) return null;
      const out = {};
      ROLES.forEach((r) => {
        const m = new RegExp(`${ROLE_INFO[r].label} (\\d+)`).exec(last.text);
        out[r] = m ? Number(m[1]) : 0;
      });
      return out;
    },
  },
};
</script>
